import React from 'react';
import BaseStep from './BaseStep';
import FiltersContainer from '../../../Filters/Filters';
import Filter from '../../../Filter/Filter';
import AssetTableComponent from './AssetSelection/AssetTable';
import AssetCarousel from './AssetSelection/AssetCarousel';
import Loader from '../api/Loader';
import LastAction from './AssetSelection/LastAction';

/**
 * @typedef {import('../types/ContentItems').ContentItem} ContentItem
 * @typedef {import('../types/User').User} User
 */

/**
 * Asset Selection step class.
 */
export default class AssetSelectionStep extends BaseStep {
  static id = 'asset-selection-step';

  static title = 'Asset selection';

  static DEFAULT_IMAGE_DURATION_MS = 5000;

  /**
   * Triggers data load for users.
   * @see super.wizardData.api_users
   * @param {string} search - The URL search query.
   */
  static triggerDataLoad(search) {
    Loader.loadUsers()
      .then(result => {
        super.wizardData.api_users = result;
      })
      .catch(error => {
        super.notification.error('Network error while loading users', error);
      });
    AssetSelectionStep.triggerSearchUpdate(search);
  }

  static #triggerSearchUpdateCounter = 0;

  /**
   * Triggers a search update for assets based on the provided search string.
   * @param {string} search - The search string to filter assets.
   * @see super.wizardData.api_assets
   */
  static triggerSearchUpdate(search) {
    // Increment the counter to indicate a new search update has been triggered
    AssetSelectionStep.#triggerSearchUpdateCounter += 1;

    // Capture the current counter value to check against when the promise resolves
    const currentCount = AssetSelectionStep.#triggerSearchUpdateCounter;

    Loader.loadAssets(search)
      .then(result => {
        if (currentCount !== AssetSelectionStep.#triggerSearchUpdateCounter) {
          // A newer search update has been triggered, so we ignore this result
          return;
        }

        super.wizardData.api_assets = result;
      })
      .catch(error => {
        super.notification.error('Network error while loading Assets', error, { search });
      });
  }

  /**
   * Generates the filters UI based on asset facets and stats.
   * Includes various filter types like text search, checkboxes, sliders, etc.
   * @returns {JSX.Element|null} The FiltersContainer component or null if no facet fields.
   */
  static getFilters() {
    const assets = super.wizardData.api_assets;
    const users = super.wizardData.api_users;

    if (!assets) {
      return null;
    }

    const facetFields = assets.facet_fields || {};
    const statsFields = assets.stats_fields || {};

    if (!Object.keys(facetFields).length) {
      return null;
    }

    const { type = [], tags = [], createdBy = [], modifiedBy = [], approved = [] } = facetFields;

    return (
      <FiltersContainer>
        <Filter type="text" name="search" title="Search" placeholder="Search assets" />
        <Filter type="checkbox" name="type" title="Type" data={type} />
        <Filter
          type="checkbox"
          name="createdBy"
          title="Created by"
          data={createdBy}
          users={users}
        />
        <Filter
          type="checkbox"
          name="modifiedBy"
          title="Modified by"
          data={modifiedBy}
          users={users}
        />
        <Filter type="checkbox" name="approved" title="Approval" data={approved} />
        <Filter type="tag" name="tags" title="Tags" data={tags} />
        {statsFields.width && (
          <Filter type="slider" name="width" title="Width" data={statsFields.width} unit="px" />
        )}
        {statsFields.height && (
          <Filter type="slider" name="height" title="Height" data={statsFields.height} unit="px" />
        )}
      </FiltersContainer>
    );
  }

  /**
   * Determines if this step has a filter button in the actions menu.
   * To be overridden by subclasses if needed.
   * @returns {boolean} True if has filter button, false otherwise.
   */
  static hasFilter() {
    return true;
  }

  /**
   * Determines if the necessary data is still loading.
   * @returns {boolean} True if assets or users data is not yet loaded, false otherwise.
   */
  static dataIsLoading() {
    return (
      AssetSelectionStep.isUsersWaitingForLoad() || AssetSelectionStep.isAssetsWaitingForLoad()
    );
  }

  /**
   * Determines if the users data is still loading.
   * @returns {boolean} True if users data is not yet loaded, false otherwise.
   */
  static isUsersWaitingForLoad() {
    return !super.wizardData.api_users.length;
  }

  /**
   * Determines if the assets data is still loading.
   * @return {boolean} True if assets data is not yet fully loaded, false otherwise.
   */
  static isAssetsWaitingForLoad() {
    return !super.wizardData.api_assets;
  }

  /**
   * Determines if the user can proceed to the next step.
   * @param {WizardData} wizardData - The wizard data object to check for validation.
   * @returns {boolean} True if the asset selection is valid, false otherwise.
   */
  static canProceed() {
    return super.wizardData.isAssetSelectionValid().isValid;
  }

  // Object methods BELOW -----------------------------------------------------

  /**
   * @param {Object} props - The component props.
   */
  constructor(props) {
    super(props);

    this.state = {
      lastAction: new LastAction(),
    };
  }

  /**
   * Handles the click event for adding an asset to the selection.
   * Adds the asset to the data and updates the context.
   * Also updates the canProceed state based on validation.
   * @param {ContentItem} asset - The asset object to add.
   * @see this.wizardData.sequence.assets
   */
  handleAssetClick = asset => {
    const lastValidationResult = this.wizardData.addAsset(
      asset,
      AssetSelectionStep.DEFAULT_IMAGE_DURATION_MS,
    );

    this.setState({
      lastAction: new LastAction(lastValidationResult.code, asset.id, asset.type),
    });
  };

  /**
   * Handles the removal of a selected asset at the given index.
   * Removes the asset from data, updates context, and refreshes canProceed state.
   * @param {number} index - The index of the asset in the selectedAssets array to remove.
   * @see this.wizardData.sequence.assets
   */
  handleRemoveSelected = index => {
    this.wizardData.removeAssetAt(index);
    this.setState({
      lastAction: new LastAction(),
    });
  };

  /**
   * Handles updating the duration of a selected asset.
   * @param {number} index - The index of the asset.
   * @param {number} duration - The new duration in seconds.
   * @see this.wizardData.sequence.assets
   */
  handleUpdateDuration = (index, duration) => {
    this.wizardData.updateAssetDuration(index, duration);
  };

  /**
   * Handles reordering of assets via drag and drop.
   * @param {number} fromIndex - Original index.
   * @param {number} toIndex - Target index.
   * @see this.wizardData.sequence.assets
   */
  handleMoveAsset = (fromIndex, toIndex) => {
    this.wizardData.moveAsset(fromIndex, toIndex);
  };

  /**
   * Handles updating the validity period of a selected asset.
   * @param {number} index - The index of the asset.
   * @param {ContentItem} updatedAsset - The asset object with updated validity information.
   * @see this.wizardData.sequence.assets
   */
  handleUpdateAsset = (index, updatedAsset) => {
    this.wizardData.updateAssetAt(index, updatedAsset);
  };

  /**
   * Renders the step component.
   * @returns {JSX.Element} The step component.
   */
  render() {
    return (
      <>
        {this.renderNavigationPrompt(true)}
        <div className="asset-selection-list">
          <AssetCarousel
            wizardData={this.wizardData}
            onRemove={this.handleRemoveSelected}
            onMove={this.handleMoveAsset}
            onDurationUpdate={this.handleUpdateDuration}
            onValidityUpdate={this.handleUpdateAsset}
          />
          <AssetTableComponent
            contentItems={this.wizardData.api_assets.content_items}
            numFound={this.wizardData.api_assets.numFound}
            selectedAssets={this.wizardData.selectedAssets}
            onClick={this.handleAssetClick}
            lastAction={this.state.lastAction}
          />
        </div>
      </>
    );
  }
}
